import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import '../styles/responsive/pages/Merchants.css';
import {
  Search,
  Filter,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  X,
  Check,
  CheckCircle,
  Users,
  TrendingUp,
  UserPlus,
} from 'lucide-react';
import { formatUSD } from '../utils/formatAmount';
import { fetchWithAuth } from '../utils/api';
import useDebounce from '../hooks/useDebounce';
import { TableSkeleton } from '../components/common/Skeleton';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Merchants = () => {
  const { token } = useSelector((state) => state.auth);
  const { mode } = useTheme();
  const navigate = useNavigate();

  const [merchants, setMerchants] = useState([]);
  const [stats, setStats] = useState({
    totalMerchants: 0,
    verifiedMerchants: 0,
    activeMerchants: 0,
    newMerchants: 0,
    totalVolume: 0,
  });
  const [isLoading, setIsLoading] = useState(true);

  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 400);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);

  const fetchMerchants = useCallback(async () => {
    if (!token) return;

    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: pageSize,
        sortBy,
        sortOrder,
      });

      params.append('isTestnet', mode === 'test' ? 'true' : 'false');

      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (debouncedSearchQuery) {
        params.append('search', debouncedSearchQuery);
      }

      const response = await fetchWithAuth(`${API_BASE_URL}/merchants?${params}`);
      const data = await response.json();

      if (data.success) {
        setMerchants(data.data.merchants);
        setTotalPages(data.data.pagination.totalPages);
        setTotalItems(data.data.pagination.total);
      }
    } catch (error) {
      console.error('Failed to fetch merchants:', error);
    } finally {
      setIsLoading(false);
    }
  }, [token, currentPage, pageSize, statusFilter, debouncedSearchQuery, sortBy, sortOrder, mode]);

  const fetchStats = useCallback(async () => {
    if (!token) return;

    try {
      const params = new URLSearchParams({
        isTestnet: mode === 'test' ? 'true' : 'false',
      });

      const response = await fetchWithAuth(`${API_BASE_URL}/merchants/stats?${params}`);
      const data = await response.json();

      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch merchant stats:', error);
    }
  }, [token, mode]);

  useEffect(() => {
    fetchMerchants();
    fetchStats();
  }, [fetchMerchants, fetchStats]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
    setShowFilterDropdown(false);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(column);
      setSortOrder('DESC');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const clearAllFilters = () => {
    setStatusFilter('all');
    setSearchQuery('');
    setSortBy('created_at');
    setSortOrder('DESC');
    setCurrentPage(1);
  };

  const statusTabs = [
    { id: 'all', label: 'All', count: stats.totalMerchants },
    { id: 'verified', label: 'Verified', count: stats.verifiedMerchants },
    { id: 'unverified', label: 'Unverified', count: stats.unverifiedMerchants || 0 },
  ];

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  return (
    <div className="merchants-page">
      <div className="rd-page-header">
        <div>
          <h1 className="rd-page-title">Merchants</h1>
          <p className="rd-page-subtitle">Manage and view all registered merchants</p>
        </div>
      </div>

      <div className="rd-kpi-grid stats-grid">
        <div className="rd-kpi stat-card">
          <div className="rd-icon-tile stat-icon">
            <Users size={20} />
          </div>
          <div className="stat-content">
            <span className="rd-kpi-label stat-title">Total Merchants</span>
            <span className="rd-kpi-value stat-value">{stats.totalMerchants}</span>
          </div>
        </div>
        <div className="rd-kpi stat-card">
          <div className="rd-icon-tile stat-icon ok">
            <CheckCircle size={20} />
          </div>
          <div className="stat-content">
            <span className="rd-kpi-label stat-title">Verified</span>
            <span className="rd-kpi-value stat-value">{stats.verifiedMerchants}</span>
          </div>
        </div>
        <div className="rd-kpi stat-card">
          <div className="rd-icon-tile stat-icon warn">
            <TrendingUp size={20} />
          </div>
          <div className="stat-content">
            <span className="rd-kpi-label stat-title">Active (30 days)</span>
            <span className="rd-kpi-value stat-value">{stats.activeMerchants}</span>
          </div>
        </div>
        <div className="rd-kpi stat-card">
          <div className="rd-icon-tile stat-icon">
            <UserPlus size={20} />
          </div>
          <div className="stat-content">
            <span className="rd-kpi-label stat-title">New (30 days)</span>
            <span className="rd-kpi-value stat-value">{stats.newMerchants}</span>
          </div>
        </div>
      </div>

      <div className="rd-tabs status-tabs">
        {statusTabs.map((tab) => (
          <button
            key={tab.id}
            className={`rd-tab status-tab ${statusFilter === tab.id ? 'active' : ''}`}
            onClick={() => handleStatusFilter(tab.id)}
          >
            <span className="tab-label">{tab.label}</span>
            <span className="rd-tab-count tab-count">{tab.count}</span>
          </button>
        ))}
      </div>

      <div className="table-toolbar">
        <div className="search-box">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            className="rd-input"
            placeholder="Search merchants by name or email..."
            value={searchQuery}
            onChange={handleSearch}
          />
          {searchQuery && (
            <button className="search-clear" onClick={() => setSearchQuery('')}>
              <X size={14} />
            </button>
          )}
        </div>
        <div className="toolbar-actions">
          <div className="dropdown-container">
            <button
              className="rd-btn rd-btn-secondary btn-toolbar"
              onClick={() => setShowFilterDropdown(!showFilterDropdown)}
            >
              <Filter size={16} />
              Sort by
            </button>
            {showFilterDropdown && (
              <div className="dropdown-menu filter-dropdown">
                <div className="dropdown-section">
                  <div className="dropdown-header">Sort By</div>
                  {[
                    { id: 'created_at', label: 'Date Joined' },
                    { id: 'last_login', label: 'Last Login' },
                    { id: 'first_name', label: 'Name' },
                    { id: 'email', label: 'Email' },
                  ].map((option) => (
                    <button
                      key={option.id}
                      className={`dropdown-item ${sortBy === option.id ? 'active' : ''}`}
                      onClick={() => {
                        handleSort(option.id);
                        setShowFilterDropdown(false);
                      }}
                    >
                      {sortBy === option.id && <Check size={14} />}
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {(statusFilter !== 'all' || searchQuery) && (
            <button className="rd-btn rd-btn-ghost btn-clear-filters" onClick={clearAllFilters}>
              <X size={14} />
              Clear all
            </button>
          )}
        </div>
      </div>

      <div className="rd-table-wrap merchants-table-container">
        {isLoading ? (
          <TableSkeleton rows={6} cols={7} />
        ) : merchants.length === 0 ? (
          <div className="rd-empty empty-state">
            <div className="rd-empty-icon"><Users size={20} /></div>
            <h3>No merchants found</h3>
            <p>Merchants will appear here when they register</p>
          </div>
        ) : (
          <>
          <table className="rd-table merchants-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('first_name')} className="sortable">
                  MERCHANT
                  {sortBy === 'first_name' && (
                    <ChevronDown
                      size={14}
                      className={sortOrder === 'ASC' ? 'rotate-180' : ''}
                    />
                  )}
                </th>
                <th>BUSINESS</th>
                <th>STATUS</th>
                <th>TRANSACTIONS</th>
                <th>VOLUME</th>
                <th onClick={() => handleSort('created_at')} className="sortable">
                  JOINED
                  {sortBy === 'created_at' && (
                    <ChevronDown
                      size={14}
                      className={sortOrder === 'ASC' ? 'rotate-180' : ''}
                    />
                  )}
                </th>
                <th onClick={() => handleSort('last_login')} className="sortable">
                  LAST LOGIN
                  {sortBy === 'last_login' && (
                    <ChevronDown
                      size={14}
                      className={sortOrder === 'ASC' ? 'rotate-180' : ''}
                    />
                  )}
                </th>
              </tr>
            </thead>
            <tbody>
              {merchants.map((merchant) => (
                <tr key={merchant.id} onClick={() => navigate(`/dashboard/merchants/${merchant.id}`)}>
                  <td className="merchant-cell">
                    <div className="merchant-info">

                      <div className="merchant-details">
                        <span className="merchant-name">
                          {merchant.firstName} {merchant.lastName}
                        </span>
                        <span className="merchant-email">{merchant.email}</span>
                      </div>
                    </div>
                  </td>
                  <td className="business-cell">
                    {merchant.businessName || '-'}
                  </td>
                  <td>
                    <span className={`rd-badge ${merchant.isVerified ? 'ok' : 'warn'}`}>
                      {merchant.isVerified ? 'Verified' : 'Unverified'}
                    </span>
                  </td>
                  <td className="stats-cell rd-mono">{merchant.stats?.totalTransactions || 0}</td>
                  <td className="stats-cell rd-mono">${formatUSD(merchant.stats?.totalVolume || 0)}</td>
                  <td className="date-cell">{formatDate(merchant.createdAt)}</td>
                  <td className="date-cell">{formatDate(merchant.lastLogin)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Mobile card list — hidden on tablet/desktop via CSS */}
          <div className="merchant-card-list">
            {merchants.map((merchant) => (
              <div
                key={merchant.id}
                className="merchant-row-card"
                onClick={() => navigate(`/dashboard/merchants/${merchant.id}`)}
              >
                <div className="merchant-row-card-top">
                  <div className="merchant-details">
                    <span className="merchant-name">
                      {merchant.firstName} {merchant.lastName}
                    </span>
                    <span className="merchant-email">{merchant.email}</span>
                  </div>
                  <span className={`rd-badge ${merchant.isVerified ? 'ok' : 'warn'}`}>
                    {merchant.isVerified ? 'Verified' : 'Unverified'}
                  </span>
                </div>
                {merchant.businessName && (
                  <div className="merchant-row-card-business">{merchant.businessName}</div>
                )}
                <div className="merchant-row-card-bottom">
                  <span className="merchant-row-card-stat rd-mono">
                    {merchant.stats?.totalTransactions || 0} txns · ${formatUSD(merchant.stats?.totalVolume || 0)}
                  </span>
                  <span className="merchant-row-card-date">{formatDate(merchant.createdAt)}</span>
                </div>
              </div>
            ))}
          </div>
          </>
        )}
      </div>

      {merchants.length > 0 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {startItem} to {endItem} of {totalItems} results
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="rd-select page-size-select"
            >
              <option value={10}>10 per page</option>
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
          </div>
          <div className="pagination-controls">
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            >
              <ChevronsLeft size={18} />
            </button>
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft size={18} />
            </button>
            <div className="page-numbers">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              <ChevronRight size={18} />
            </button>
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            >
              <ChevronsRight size={18} />
            </button>
          </div>
        </div>
      )}

      <style>{`
        .merchants-page { padding: 0; }

        /* Stats grid — KPI cards with a small leading icon tile */
        .merchants-page .stats-grid { margin-bottom: 24px; }
        .merchants-page .rd-kpi.stat-card {
          display: flex; align-items: center; gap: 14px; padding: 18px 20px;
        }
        .merchants-page .stat-icon { flex-shrink: 0; }
        .merchants-page .stat-icon.ok { background: var(--ok-soft); color: var(--ok); }
        .merchants-page .stat-icon.warn { background: var(--warn-soft); color: var(--warn); }
        .merchants-page .stat-content { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
        .merchants-page .rd-kpi-value.stat-value { margin-top: 0; font-size: 22px; }

        /* Status tabs (composed on .rd-tabs) */
        .merchants-page .status-tabs { margin-bottom: 16px; }
        .merchants-page .status-tab { display: inline-flex; align-items: center; gap: 8px; }

        /* Toolbar */
        .merchants-page .table-toolbar {
          display: flex; justify-content: space-between; align-items: center;
          gap: 12px; margin-bottom: 16px; flex-wrap: wrap;
        }
        .merchants-page .search-box { position: relative; flex: 1; max-width: 400px; }
        .merchants-page .search-box .rd-input { padding-left: 38px; }
        .merchants-page .search-icon {
          position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--ink-muted);
        }
        .merchants-page .search-clear {
          position: absolute; right: 8px; top: 50%; transform: translateY(-50%);
          background: transparent; border: none; color: var(--ink-muted); cursor: pointer; padding: 4px;
        }
        .merchants-page .toolbar-actions { display: flex; gap: 8px; align-items: center; }

        /* Sort dropdown */
        .merchants-page .dropdown-container { position: relative; }
        .merchants-page .dropdown-menu {
          position: absolute; top: 100%; right: 0; margin-top: 6px;
          background: var(--bg-surface); border: 1px solid var(--line); border-radius: var(--r-md);
          box-shadow: var(--sh-pop); z-index: 100; min-width: 180px; padding: 6px;
        }
        .merchants-page .dropdown-header {
          padding: 6px 8px; font-size: 11px; font-weight: 600; color: var(--ink-muted);
          text-transform: uppercase; letter-spacing: 0.04em;
        }
        .merchants-page .dropdown-item {
          display: flex; align-items: center; gap: 8px; width: 100%; padding: 8px;
          background: transparent; border: none; border-radius: var(--r-sm);
          font-size: 14px; color: var(--ink); cursor: pointer; text-align: left;
        }
        .merchants-page .dropdown-item:hover { background: var(--bg-inset); }
        .merchants-page .dropdown-item.active { color: var(--accent-ink); }

        /* Table cells */
        .merchants-page .merchants-table th.sortable { cursor: pointer; }
        .merchants-page .merchants-table th.sortable:hover { color: var(--ink-secondary); }
        .merchants-page .merchants-table th svg { margin-left: 4px; vertical-align: middle; }
        .merchants-page .merchants-table th svg.rotate-180 { transform: rotate(180deg); }
        .merchants-page .merchants-table tbody tr { cursor: pointer; }
        .merchants-page .merchant-cell { min-width: 200px; }
        .merchants-page .merchant-details { display: flex; flex-direction: column; }
        .merchants-page .merchant-name { font-weight: 600; color: var(--ink); }
        .merchants-page .merchant-email { font-size: 12.5px; color: var(--ink-muted); }
        .merchants-page .business-cell {
          max-width: 150px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .merchants-page .date-cell { color: var(--ink-secondary); white-space: nowrap; }

        /* Pagination */
        .merchants-page .pagination-container {
          display: flex; justify-content: space-between; align-items: center;
          padding: 16px 2px; margin-top: 12px; flex-wrap: wrap; gap: 12px;
        }
        .merchants-page .pagination-info {
          display: flex; align-items: center; gap: 14px; font-size: 13.5px; color: var(--ink-secondary);
        }
        .merchants-page .page-size-select { width: auto; padding: 7px 10px; font-size: 13px; }
        .merchants-page .pagination-controls { display: flex; align-items: center; gap: 4px; }
        .merchants-page .pagination-btn {
          width: 36px; height: 36px; background: var(--bg-surface); border: 1px solid var(--line-strong);
          border-radius: var(--r-sm); color: var(--ink-secondary); cursor: pointer;
          display: flex; align-items: center; justify-content: center; transition: all 0.15s;
        }
        .merchants-page .pagination-btn:hover:not(:disabled) { border-color: var(--ink); color: var(--ink); }
        .merchants-page .pagination-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .merchants-page .page-numbers { display: flex; align-items: center; gap: 4px; margin: 0 4px; }
        .merchants-page .page-number {
          min-width: 36px; height: 36px; display: flex; align-items: center; justify-content: center;
          background: var(--bg-surface); border: 1px solid var(--line-strong); border-radius: var(--r-sm);
          font-size: 14px; font-weight: 600; color: var(--ink-secondary); cursor: pointer; transition: all 0.15s;
        }
        .merchants-page .page-number:hover:not(.active) { border-color: var(--ink); color: var(--ink); }
        .merchants-page .page-number.active {
          background: var(--primary); border-color: var(--primary); color: #fff;
        }
      `}</style>
    </div>
  );
};

export default Merchants;
